import { Sale, ShippingService, PickupStore, ShippingAddress, CheckoutCustomerData, CheckoutSession, getLatestCheckoutSession } from "@/hooks/useSales";
import { Product } from "@/hooks/useProducts";

const escapeHtml = (str: string) =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const asObject = <T>(value: unknown): T | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as T;
};

interface AddressParts {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  cep?: string | null;
}

const formatAddressLines = (address: AddressParts) => {
  const line1 = [address.street, address.number].filter(Boolean).join(", ");
  const line1WithComplement = [line1, address.complement].filter(Boolean).join(" - ");
  const cityState = [address.city, address.state].filter(Boolean).join("/");
  const line2 = [address.neighborhood, cityState].filter(Boolean).join(" — ");
  return [line1WithComplement, line2, address.cep].filter(Boolean);
};

const resolveDeliveryAddress = (sale: Sale, session: CheckoutSession | null): AddressParts | null => {
  const shipping = asObject<ShippingAddress>(session?.shipping_address);
  const sessionCustomer = asObject<CheckoutCustomerData>(session?.customer_data);
  const customer = sale.customer;

  const parts: AddressParts = {
    street: shipping?.street || customer?.street || sessionCustomer?.street || null,
    number: shipping?.number || customer?.number || sessionCustomer?.number || null,
    complement: shipping?.complement || customer?.complement || sessionCustomer?.complement || null,
    neighborhood: shipping?.neighborhood || customer?.neighborhood || sessionCustomer?.neighborhood || null,
    city: shipping?.city || customer?.city || sessionCustomer?.city || null,
    state: shipping?.state || customer?.state || sessionCustomer?.state || null,
    cep: shipping?.cep || customer?.cep || sessionCustomer?.cep || null,
  };

  return Object.values(parts).some(Boolean) ? parts : null;
};

const pickupStoreToAddress = (store: PickupStore): AddressParts => ({
  street: store.street,
  number: store.number,
  complement: store.complement,
  neighborhood: store.neighborhood,
  city: store.city,
  state: store.state,
  cep: store.cep,
});

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const SALES_CHANNELS: Record<string, string> = {
  story_shop: "Story Shop",
  live: "Live",
  manual: "Manual",
};

const DELIVERY_METHODS: Record<string, string> = {
  pickup: "Retirada na loja",
  delivery: "Entrega",
};

const PAYMENT_METHODS: Record<string, string> = {
  pix: "Pix",
  card: "Cartão",
  checkout: "Checkout (Link)",
  cash: "Dinheiro",
  outro: "Outro",
};

const PAYMENT_STATUS: Record<string, string> = {
  pending: "Pendente",
  paid: "Pago",
  cancelled: "Cancelado",
  pay_on_pickup: "Pgto na Retirada",
};

export function printSale(sale: Sale, products: Product[]) {
  const checkoutSession = getLatestCheckoutSession(sale.checkout_sessions);
  const shippingCost = checkoutSession?.shipping_cost
    ? Number(checkoutSession.shipping_cost)
    : 0;
  const shippingServiceName = (checkoutSession?.shipping_service as ShippingService | null)?.name;

  const pickupStore = checkoutSession?.pickup_store as PickupStore | null | undefined;
  const isPickup = sale.delivery_method === "pickup";
  const addressParts = isPickup && pickupStore
    ? pickupStoreToAddress(pickupStore)
    : resolveDeliveryAddress(sale, checkoutSession);
  const addressLines = addressParts ? formatAddressLines(addressParts) : [];
  const addressTitle = isPickup ? "Endereço de Retirada" : "Endereço de Entrega";
  const addressBlock = addressLines.length
    ? `<hr class="divider"/>
       <div class="section-title">${addressTitle}${isPickup && pickupStore?.name ? ` — ${escapeHtml(pickupStore.name)}` : ""}</div>
       <div class="address-block">
         ${addressLines.map((line) => `<div class="address-line">${escapeHtml(line)}</div>`).join("")}
       </div>`
    : "";

  const itemsRows = (sale.sale_items || [])
    .map((item) => {
      const product = products.find((p) => p.id === item.product_id);
      const imageUrl = product?.image_urls?.[0];
      const productCode = (item as any).product_code || product?.product_code;

      return `
      <tr>
        <td class="cell-product">
          <div class="product-row">
            ${
              imageUrl
                ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(item.product_name)}" class="product-img"/>`
                : `<div class="product-img-placeholder">
                     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                   </div>`
            }
            <div class="product-info">
              <span class="product-name">${escapeHtml(item.product_name)}</span>
              ${productCode ? `<span class="product-code">Cód: ${escapeHtml(productCode)}</span>` : ""}
              ${item.variation_description ? `<span class="product-variation">${escapeHtml(item.variation_description)}</span>` : ""}
            </div>
          </div>
        </td>
        <td class="cell-center">${item.quantity}</td>
        <td class="cell-right">${formatCurrency(Number(item.unit_price))}</td>
        <td class="cell-right cell-bold">${formatCurrency(Number(item.total_price))}</td>
      </tr>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Venda #${sale.id.slice(0, 8).toUpperCase()}</title>
  <style>
    @page {
      size: A4;
      margin: 20mm 18mm;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #111;
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 20mm 18mm;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    h1 { font-size: 22px; margin-bottom: 4px; }
    .sale-id { color: #6b7280; font-size: 13px; font-family: monospace; }
    .date { color: #6b7280; font-size: 13px; margin-top: 4px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; }

    .divider { border: none; border-top: 1px solid #e5e7eb; margin: 18px 0; }

    .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
    .info-box { background: #f9fafb; border-radius: 6px; padding: 10px 14px; }
    .info-label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
    .info-value { font-size: 14px; font-weight: 500; margin-top: 2px; }

    .section-title { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; font-weight: 600; }

    .customer-name { font-size: 15px; font-weight: 600; }
    .customer-info { color: #6b7280; font-size: 13px; margin-top: 4px; }

    .address-block { font-size: 13px; color: #374151; line-height: 1.5; }
    .address-line { margin-top: 2px; }

    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    thead th {
      padding: 10px 14px;
      text-align: left;
      font-size: 11px;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      border-bottom: 2px solid #e5e7eb;
      font-weight: 600;
    }
    thead th:nth-child(2) { text-align: center; }
    thead th:nth-child(3), thead th:nth-child(4) { text-align: right; }

    tbody tr { break-inside: avoid; }
    tbody td { padding: 12px 14px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }

    .cell-center { text-align: center; }
    .cell-right { text-align: right; }
    .cell-bold { font-weight: 600; }
    .cell-product { padding: 10px 14px; }

    .product-row { display: flex; align-items: center; gap: 14px; }
    .product-img {
      width: 56px;
      height: 56px;
      border-radius: 8px;
      object-fit: cover;
      flex-shrink: 0;
      border: 1px solid #e5e7eb;
    }
    .product-img-placeholder {
      width: 56px;
      height: 56px;
      border-radius: 8px;
      background: #f3f4f6;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .product-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    .product-name { font-weight: 600; font-size: 14px; }
    .product-code { font-size: 11px; color: #6b7280; font-family: monospace; }
    .product-variation {
      font-size: 12px;
      color: #4b5563;
      background: #f3f4f6;
      padding: 2px 8px;
      border-radius: 4px;
      display: inline-block;
      margin-top: 2px;
    }

    .totals { background: #f9fafb; border-radius: 8px; padding: 18px; }
    .total-row { display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 6px; }
    .total-row .label { color: #6b7280; }
    .total-final {
      display: flex;
      justify-content: space-between;
      font-size: 20px;
      font-weight: 700;
      border-top: 2px solid #e5e7eb;
      padding-top: 12px;
      margin-top: 12px;
    }

    .notes { background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 12px; font-size: 13px; }

    .badge { display: inline-block; padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; }
    .badge-paid { background: #dcfce7; color: #166534; }
    .badge-pending { background: #f3f4f6; color: #374151; }
    .badge-cancelled { background: #fee2e2; color: #991b1b; }
    .badge-pickup { background: #fef3c7; color: #92400e; }

    @media print {
      body { padding: 0; width: auto; }
    }
    @media screen {
      body { box-shadow: 0 0 20px rgba(0,0,0,0.1); }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1>Comprovante de Venda</h1>
      <span class="sale-id">#${sale.id.slice(0, 8).toUpperCase()}</span>
      <div class="date">${formatDate(sale.created_at)}</div>
    </div>
    <span class="badge ${sale.payment_status === "paid" ? "badge-paid" : sale.payment_status === "cancelled" ? "badge-cancelled" : sale.payment_status === "pay_on_pickup" ? "badge-pickup" : "badge-pending"}">
      ${sale.payment_status ? PAYMENT_STATUS[sale.payment_status] || sale.payment_status : "—"}
    </span>
  </div>

  <hr class="divider"/>

  <div class="info-grid">
    <div class="info-box">
      <div class="info-label">Canal</div>
      <div class="info-value">${sale.sales_channel ? SALES_CHANNELS[sale.sales_channel] || sale.sales_channel : "—"}</div>
    </div>
    <div class="info-box">
      <div class="info-label">Entrega</div>
      <div class="info-value">${(() => {
        if (!sale.delivery_method) return "—";
        const label = DELIVERY_METHODS[sale.delivery_method] || sale.delivery_method;
        const pickupStore = getLatestCheckoutSession(sale.checkout_sessions)?.pickup_store as PickupStore | null | undefined;
        if (sale.delivery_method === "pickup" && pickupStore?.name) {
          return `${label} — ${escapeHtml(pickupStore.name)}`;
        }
        return label;
      })()}</div>
    </div>
    <div class="info-box">
      <div class="info-label">Pagamento</div>
      <div class="info-value">${sale.payment_method ? PAYMENT_METHODS[sale.payment_method] || sale.payment_method : "—"}</div>
    </div>
  </div>

  <hr class="divider"/>

  <div class="section-title">Cliente</div>
  ${
    sale.customer
      ? `<div class="customer-name">${escapeHtml(sale.customer.name || "Sem nome")}</div>
         <div class="customer-info">
           ${sale.customer.email ? escapeHtml(sale.customer.email) : ""}
           ${sale.customer.email && sale.customer.whatsapp ? " · " : ""}
           ${sale.customer.whatsapp ? escapeHtml(sale.customer.whatsapp) : ""}
         </div>`
      : `<div style="color:#6b7280;font-size:13px;">Venda sem cliente vinculado</div>`
  }

  ${addressBlock}

  <hr class="divider"/>

  <div class="section-title">Produtos</div>
  <table>
    <thead>
      <tr>
        <th>Produto</th>
        <th>Qtd</th>
        <th>Preço</th>
        <th>Total</th>
      </tr>
    </thead>
    <tbody>
      ${itemsRows || `<tr><td colspan="4" style="padding:20px;text-align:center;color:#6b7280;">Nenhum produto</td></tr>`}
    </tbody>
  </table>

  <hr class="divider"/>

  <div class="totals">
    <div class="total-row">
      <span class="label">Subtotal</span>
      <span>${formatCurrency(Number(sale.subtotal))}</span>
    </div>
    ${
      shippingCost > 0
        ? `<div class="total-row">
             <span class="label">Frete${shippingServiceName ? ` (${escapeHtml(shippingServiceName)})` : ""}</span>
             <span>${formatCurrency(shippingCost)}</span>
           </div>`
        : ""
    }
    ${
      sale.discount && Number(sale.discount) > 0
        ? `<div class="total-row">
             <span class="label">Desconto</span>
             <span>-${formatCurrency(Number(sale.discount))}</span>
           </div>`
        : ""
    }
    <div class="total-final">
      <span>Total</span>
      <span>${formatCurrency(Number(sale.total))}</span>
    </div>
  </div>

  ${
    sale.notes
      ? `<hr class="divider"/>
         <div class="section-title">Observações</div>
         <div class="notes">${escapeHtml(sale.notes)}</div>`
      : ""
  }

  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}
